import { useEffect, useMemo, useState } from "react";

import { fetchLast24HoursPendingCases } from "../../store/allocation/readyCasesSlice";

import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { isEmployeeInvolvedInCase } from "../../utils/employeeNames";
import MyCasesToggle, {
  useMyCasesToggle,
} from "../../components/allocation-dashboard/MyCasesToggle";

import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

import UploadStatusBadge from "../../components/allocation-dashboard/UploadStatusBadge";
import UploadStatusLegend from "../../components/allocation-dashboard/UploadStatusLegend";
import { formatDateTimeIST } from "../../utils/dateTime";
import { getUploadStatusInfo } from "../../utils/uploadStatus";
import { RedesignScopeTag } from "../../components/admin/unitsTableShared";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminSearchInput,
  AdminSelect,
  AdminMetaRow,
  AdminClearFilters,
  AdminTableCard,
  AdminTableState,
  adminTableHead,
  adminTableCell,
} from "../../components/admin/AdminPageShell";

const AllocationLogs = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { loading, error, last24HoursCases } = useAppSelector(
    (state) => state.readycases,
  );
  const { showToggle, myCasesOnly, enabled: myCasesEnabled, setEnabled: setMyCasesEnabled } =
    useMyCasesToggle(employee);

  const visibleCases = useMemo(
    () =>
      (last24HoursCases || []).filter((item) =>
        myCasesOnly
          ? isEmployeeInvolvedInCase(item, employee?.emp_name)
          : true,
      ),
    [last24HoursCases, employee?.emp_name, myCasesOnly],
  );

  const [search, setSearch] = useState("");
  const [uploadFilter, setUploadFilter] = useState<
    "ALL" | "PENDING" | "UPLOADED"
  >("ALL");

  useEffect(() => {
    dispatch(fetchLast24HoursPendingCases());
  }, [dispatch]);

  const uploadCounts = useMemo(() => {
    const pending = visibleCases.filter(
      (c) => c.upload_status === "Ready to Upload",
    ).length;
    const uploaded = visibleCases.filter((c) =>
      c.upload_status?.startsWith("Uploaded"),
    ).length;
    return { pending, uploaded };
  }, [visibleCases]);

  const filteredData = useMemo(() => {
    let data = visibleCases;

    if (uploadFilter === "PENDING") {
      data = data.filter((item) => item.upload_status === "Ready to Upload");
    } else if (uploadFilter === "UPLOADED") {
      data = data.filter((item) => item.upload_status?.startsWith("Uploaded"));
    }

    if (search) {
      data = data.filter((item) =>
        item.CaseId?.toLowerCase().includes(search.toLowerCase()),
      );
    }

    return data;
  }, [visibleCases, search, uploadFilter]);

  const hasActiveFilters = Boolean(search.trim()) || uploadFilter !== "ALL";
  const clearFilters = () => {
    setSearch("");
    setUploadFilter("ALL");
  };

  const colCount = 13;

  return (
    <>
      <Helmet>
        <title>24 Hour Cases</title>
      </Helmet>

      <div className="p-4">
        <AdminPageShell>
          <AdminPageHeader
            title="Cases Allocated in Last 24 Hours"
            subtitle="Review upload status for cases allocated in the last day."
          >
            <AdminSearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by Case ID…"
              className="w-full sm:w-[220px]"
            />
            <AdminSelect
              value={uploadFilter}
              onChange={(value) =>
                setUploadFilter(value as "ALL" | "PENDING" | "UPLOADED")
              }
            >
              <option value="ALL">All Upload Status</option>
              <option value="PENDING">
                Pending Upload ({uploadCounts.pending})
              </option>
              <option value="UPLOADED">
                Uploaded ({uploadCounts.uploaded})
              </option>
            </AdminSelect>
            <MyCasesToggle
              show={showToggle}
              enabled={myCasesEnabled}
              empName={employee?.emp_name}
              onChange={setMyCasesEnabled}
            />
            <button
              type="button"
              onClick={() => navigate("/dashboard/allocation/unallocated")}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-white/15 border border-white/25 text-white px-4 py-2.5 text-xs font-medium hover:bg-white/20 transition"
            >
              <FaArrowLeft className="text-[10px]" />
              Back to allocation
            </button>
          </AdminPageHeader>

          <AdminMetaRow
            left={
              <>
                {loading ? (
                  <span>Loading cases…</span>
                ) : (
                  <span>
                    {filteredData.length} case
                    {filteredData.length === 1 ? "" : "s"}
                    {hasActiveFilters ? " matched" : " in the last 24 hours"}
                  </span>
                )}
                {hasActiveFilters ? (
                  <AdminClearFilters onClick={clearFilters} />
                ) : null}
              </>
            }
          />

          <UploadStatusLegend />

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              Error: {error}
            </div>
          )}

          {!loading && !error && (
            <AdminTableCard>
              <table className="w-full min-w-[1200px] table-auto">
                <thead>
                  <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                    <th className={adminTableHead}>S.No</th>
                    <th className={adminTableHead}>Case ID</th>
                    <th className={adminTableHead}>Status</th>
                    <th className={adminTableHead}>Upload Status</th>
                    <th className={adminTableHead}>Allocation Status</th>
                    <th className={adminTableHead}>Case Created</th>
                    <th className={adminTableHead}>Allocated By</th>
                    <th className={adminTableHead}>Captain</th>
                    <th className={adminTableHead}>Designer</th>
                    <th className={adminTableHead}>QC1</th>
                    <th className={adminTableHead}>QC2</th>
                    <th className={adminTableHead}>Allocation Created</th>
                    <th className={adminTableHead}>Allocation Updated</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredData.length === 0 ? (
                    <AdminTableState
                      colSpan={colCount}
                      variant="empty"
                      message="No data found"
                    />
                  ) : (
                    filteredData.map((item, index) => {
                      const uploadInfo = getUploadStatusInfo(
                        item.upload_status,
                      );
                      return (
                        <tr
                          key={index}
                          className={`border-b border-[#F0F0EC] last:border-0 transition-colors ${
                            uploadInfo.rowClass || "hover:bg-[#FAFAF8]"
                          }`}
                        >
                          <td className={adminTableCell}>{index + 1}</td>

                          <td className={adminTableCell}>
                            <div className="flex flex-col items-start gap-1">
                              <span>{item.CaseId || "-"}</span>
                              <RedesignScopeTag labels={item.redesign_category} />
                            </div>
                          </td>

                          <td className={adminTableCell}>
                            {item.status || "-"}
                          </td>

                          <td className={adminTableCell}>
                            <UploadStatusBadge status={item.upload_status} />
                          </td>

                          <td className={adminTableCell}>
                            {item.allocation_status || "-"}
                          </td>

                          <td className={adminTableCell}>
                            {formatDateTimeIST(item.CreationTime)}
                          </td>

                          <td className={adminTableCell}>
                            {item.allocated_by || "-"}
                          </td>

                          <td className={adminTableCell}>
                            {item.allocated_Captain || "-"}
                          </td>

                          <td className={adminTableCell}>
                            {item.allocated_Designer || "-"}
                          </td>

                          <td className={adminTableCell}>{item.QC1 || "-"}</td>

                          <td className={adminTableCell}>{item.QC2 || "-"}</td>

                          <td className={adminTableCell}>
                            {formatDateTimeIST(item.allocation_created_at)}
                          </td>

                          <td className={adminTableCell}>
                            {formatDateTimeIST(item.allocation_updated_at)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </AdminTableCard>
          )}
        </AdminPageShell>
      </div>
    </>
  );
};

export default AllocationLogs;
